/**
 * V2 Share cards — any content page as pictures worth posting.
 *
 * Instagram (and the photo-first half of every share sheet) ignores bare
 * URLs, so the "Share photos" intent hands over rendered CARDS: a cover
 * (polaroid photo, handwritten title, meta, optional YOINKED stamp and
 * byline) plus one quiet polaroid per extra photo — shared as files via
 * Web Share Level 2 (`navigator.share({ files })`, supported by
 * iPad/iPhone Safari). TikTok's and Instagram's composers ingest a
 * multi-file share as a photo-mode carousel. The link rides along as
 * share text, the paste-ready caption goes on the clipboard, and every
 * capability gap falls back a rung: full set → cover only → URL share →
 * clipboard.
 *
 * Any page can share cards by describing itself as a SOURCE:
 *   { baseName, title, metaLine, byline, stampDate, category, photos, hashtags }
 * (photos = storage paths / full URLs, or { src, caption } entries when
 * a follower card should say what it shows — e.g. a profile's stacks,
 * "In-N-Out · 382 places" — instead of the default "2 / 10" counter;
 * the first entry is the cover; stampDate present = the cover gets the
 * YOINKED stamp). The yoink wrappers at the bottom build the source
 * from a yoink row.
 *
 * 1080×1350 (4:5): full-bleed in an Instagram feed post, unclipped in
 * Stories, TikTok photo mode, Facebook, and Messages.
 */
import { imageSrc } from '../core/photo.js';
import { slugify } from '../../assets/js/shared/slugify.js';
import { showToast } from './toast.js';
import { categoryIconSvg } from './icons.js';

/**
 * The handshake between the Log form and the yoink page: Log writes the
 * new yoink's id here, the yoink page turns it into the share moment
 * (sessionStorage so a texted link or a reload never re-celebrates).
 */
export const JUST_CREATED_KEY = 'yoink_just_created';

const CARD_W = 1080;
const CARD_H = 1350;

// The atlas palette (src/styles/tokens.css) — canvas can't read CSS
// custom properties, so the values are mirrored here.
const PAPER = '#fdf8ec';
const PAPER_BLANK = '#f4ecda';
const INK = '#3d2f1f';
const INK_SOFT = '#6b5a42';
const GREEN = '#1f4a32';
const STAMP_RED = '#a93226';

const FONT_HAND = 'Caveat, cursive';
const FONT_SANS = '"Source Sans 3", -apple-system, sans-serif';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function stampDate(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Break text into at most maxLines lines that fit maxWidth under the
 * ctx's current font, ellipsizing the last line when it can't all fit.
 * Exported for tests.
 */
export function wrapText(ctx, text, maxWidth, maxLines = 2) {
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const attempt = line ? `${line} ${word}` : word;
    if (!line || ctx.measureText(attempt).width <= maxWidth) {
      line = attempt;
      continue;
    }
    lines.push(line);
    line = word;
    if (lines.length === maxLines) {
      let last = lines[maxLines - 1];
      while (last && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
      lines[maxLines - 1] = `${last}…`;
      return lines;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

/** Webfonts must be resolved before fillText or the card renders in Times. */
async function loadFonts() {
  if (!document.fonts?.load) return;
  try {
    await Promise.all([
      document.fonts.load(`600 84px ${FONT_HAND}`),
      document.fonts.load(`600 54px ${FONT_HAND}`),
      document.fonts.load(`600 38px ${FONT_SANS}`),
      document.fonts.load(`700 46px ${FONT_SANS}`),
    ]);
  } catch { /* fallback fonts still draw a usable card */ }
}

/** CamelCase one name into a hashtag: "Fish Tacos" → "#FishTacos". */
function tagFromName(name) {
  const tag = String(name || '').split(/[^\p{L}\p{N}]+/u).filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1)).join('');
  return tag ? `#${tag}` : '';
}

/**
 * A yoink's hashtags: its category as a CamelCase tag plus the brand
 * tag — so the right crowd finds it. Exported for tests.
 */
export function hashtags(yoink) {
  const tags = [];
  const tag = tagFromName(yoink.categories?.name);
  if (tag) tags.push(tag);
  tags.push('#YoinkAdventures');
  return tags;
}

/**
 * The paste-ready caption for any source: title + hashtags, then the
 * URL on its own line. TikTok/IG captions don't LINK the URL (TikTok's
 * "Add link" composer field is the clickable slot there), but it
 * pastes clickable into Facebook posts, YouTube descriptions, blogs.
 */
export function sourceCaption(source, url = '') {
  const base = `${source.title} — ${source.hashtags.join(' ')}`;
  return url ? `${base}\n${url}` : base;
}

function photoPaths(yoink) {
  return (Array.isArray(yoink.images) ? yoink.images : []).filter(Boolean);
}

/**
 * One photo as a drawable Image, or null (load failure, or slower than
 * the share moment should ever wait).
 */
function loadPhoto(path, timeoutMs = 8000) {
  const url = imageSrc(path);
  return new Promise((resolve) => {
    const img = new Image();
    // Supabase storage sends Access-Control-Allow-Origin: * — without
    // crossOrigin the draw would taint the canvas and toBlob throws
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => resolve(null), timeoutMs);
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = url;
  });
}

/**
 * The category's drawn ink mark as a rasterizable Image for photoless
 * covers (data: URIs never taint the canvas). null on failure — the
 * blank paper still holds the frame on its own.
 */
function loadIconImage(category, timeoutMs = 3000) {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = `data:image/svg+xml;utf8,${encodeURIComponent(categoryIconSvg(category, { size: 240, color: INK_SOFT }))}`;
  });
}

/** drawImage with CSS object-fit:cover semantics (center crop). */
function drawCover(ctx, img, x, y, w, h) {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
}

function drawStamp(ctx, dateText) {
  ctx.save();
  ctx.translate(790, 190);
  ctx.rotate(-0.21); // ~-12° — stamps land crooked
  ctx.globalAlpha = 0.88;
  ctx.strokeStyle = STAMP_RED;
  ctx.lineWidth = 6;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-160, -62, 320, 124, 14);
    ctx.stroke();
  } else {
    ctx.strokeRect(-160, -62, 320, 124);
  }
  ctx.fillStyle = STAMP_RED;
  ctx.textAlign = 'center';
  ctx.font = `700 46px ${FONT_SANS}`;
  ctx.fillText('Y O I N K E D', 0, dateText ? -6 : 14);
  if (dateText) {
    ctx.font = `600 30px ${FONT_SANS}`;
    ctx.fillText(dateText, 0, 38);
  }
  ctx.restore();
}

/**
 * Draw one card. The cover carries the story (title, stamp when the
 * source is a dated event, byline, meta); follower cards in a set stay
 * quiet — the photo, a "2 / 5" counter, and the wordmark ("stamps mark
 * events, they don't decorate").
 * @param {object} variant - { cover: boolean, caption?: string }
 */
function drawCard(ctx, source, photo, variant = { cover: true }, iconImg = null) {
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  // The passport-page rule line
  ctx.save();
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = INK_SOFT;
  ctx.lineWidth = 3;
  ctx.strokeRect(28, 28, CARD_W - 56, CARD_H - 56);
  ctx.restore();

  // Polaroid: white frame, photo, handwriting on the bottom lip
  const px = 100;
  const py = 120;
  const pw = 880;
  const frame = 30;
  const photoH = 780;

  ctx.save();
  ctx.translate(px + pw / 2, py + 470);
  ctx.rotate(-0.026); // the gentle tilt every polaroid in the app has
  ctx.translate(-(px + pw / 2), -(py + 470));

  ctx.font = `600 84px ${FONT_HAND}`;
  const titleLines = variant.cover
    ? wrapText(ctx, source.title, pw - frame * 2 - 20, 2)
    : [];
  const lipH = variant.cover ? 70 + titleLines.length * 92 : 150;
  const ph = frame + photoH + lipH;

  ctx.save();
  ctx.shadowColor = 'rgba(60, 40, 15, 0.3)';
  ctx.shadowBlur = 26;
  ctx.shadowOffsetX = 6;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(px, py, pw, ph);
  ctx.restore();

  if (photo) {
    drawCover(ctx, photo, px + frame, py + frame, pw - frame * 2, photoH);
  } else {
    // No photo — blank paper with the category's ink mark holding the frame
    ctx.fillStyle = PAPER_BLANK;
    ctx.fillRect(px + frame, py + frame, pw - frame * 2, photoH);
    if (iconImg) {
      const markSize = 260;
      ctx.drawImage(iconImg, CARD_W / 2 - markSize / 2, py + frame + photoH / 2 - markSize / 2, markSize, markSize);
    }
  }

  ctx.textAlign = 'center';
  if (variant.cover) {
    ctx.fillStyle = INK;
    ctx.font = `600 84px ${FONT_HAND}`;
    titleLines.forEach((line, i) => {
      ctx.fillText(line, px + pw / 2, py + frame + photoH + 96 + i * 92);
    });
  } else if (variant.caption) {
    // Counters and stack labels stay in the data sans; one line, ellipsized
    ctx.fillStyle = INK_SOFT;
    ctx.font = `600 44px ${FONT_SANS}`;
    const [line] = wrapText(ctx, variant.caption, pw - frame * 2 - 20, 1);
    ctx.fillText(line, px + pw / 2, py + frame + photoH + 92);
  }
  ctx.restore();

  if (variant.cover) {
    if (source.metaLine) {
      ctx.fillStyle = INK_SOFT;
      ctx.textAlign = 'center';
      ctx.font = `600 38px ${FONT_SANS}`;
      ctx.fillText(source.metaLine, CARD_W / 2, 1198);
    }
    // Attribution travels with the card even when someone else (e.g.
    // the Yoink account) shares it onward
    if (source.byline) {
      ctx.fillStyle = INK;
      ctx.textAlign = 'center';
      ctx.font = `600 54px ${FONT_HAND}`;
      ctx.fillText(source.byline, CARD_W / 2, 1256);
    }
    if (source.stampDate) drawStamp(ctx, stampDate(source.stampDate));
  }

  ctx.fillStyle = GREEN;
  ctx.textAlign = 'center';
  ctx.font = `600 36px ${FONT_SANS}`;
  ctx.fillText('yoinkadventures.com', CARD_W / 2, 1314);
}

/** Draw one card on a fresh canvas and deliver it as a named JPEG File. */
async function renderCard(source, photo, variant, filename) {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const iconImg = photo ? null : await loadIconImage(source.category);
  drawCard(ctx, source, photo, variant, iconImg);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
  if (!blob) return null;
  return new File([blob], filename, { type: 'image/jpeg' });
}

/**
 * Render a source's full card set: the cover, then one quiet polaroid
 * per remaining photo — the carousel TikTok/Instagram photo mode
 * builds from a multi-file share. Photos that fail to load drop out
 * of the set instead of holding it up.
 * @returns {Promise<File[]>} cover first; empty when nothing rendered.
 */
export async function buildCardSet(source) {
  try {
    await loadFonts();
    const entries = source.photos.map((p) => (typeof p === 'string' ? { src: p, caption: '' } : p));
    if (entries.length < 2) {
      const photo = entries[0] ? await loadPhoto(entries[0].src) : null;
      const single = await renderCard(source, photo, { cover: true }, `${source.baseName}.jpg`);
      return single ? [single] : [];
    }
    const photos = await Promise.all(entries.map((e) => loadPhoto(e.src)));
    const files = [];
    const cover = await renderCard(source, photos[0], { cover: true }, `${source.baseName}-1.jpg`);
    if (cover) files.push(cover);
    for (let i = 1; i < photos.length; i++) {
      if (!photos[i]) continue;
      const card = await renderCard(
        source, photos[i],
        { cover: false, caption: entries[i].caption || `${i + 1} / ${entries.length}` },
        `${source.baseName}-${i + 1}.jpg`
      );
      if (card) files.push(card);
    }
    return files;
  } catch {
    return [];
  }
}

/**
 * Share a source as cards, degrading a rung at a time: full card set +
 * link → cover card + link → link via share sheet → link to clipboard.
 *
 * @param {Promise<File[]|File|null>} [prebuilt] - cards started earlier
 *   (callers pre-render so the tap→sheet gap stays inside Safari's
 *   user-activation window)
 * @returns {Promise<'shared'|'dismissed'|'copied'|'failed'>}
 */
export async function shareCardSet(source, url, prebuilt = null) {
  let files = [];
  try {
    const built = await (prebuilt || buildCardSet(source));
    files = (Array.isArray(built) ? built : [built]).filter(Boolean);
  } catch { /* keep going with the URL rungs */ }

  if (files.length && navigator.canShare) {
    // The whole album where the platform takes it, the cover where not
    const payload = navigator.canShare({ files }) ? files
      : navigator.canShare({ files: [files[0]] }) ? [files[0]]
      : null;
    if (payload) {
      // TikTok and Instagram deliberately DROP prefilled text from share
      // extensions, so the paste-ready caption goes on the CLIPBOARD —
      // and it must go on BEFORE the sheet opens: navigator.share()
      // consumes the tap's user activation, and Safari refuses clipboard
      // writes after it (confirmed on iPad, 2026-07-15).
      let captionCopied = false;
      try {
        await navigator.clipboard.writeText(sourceCaption(source, url));
        captionCopied = true;
      } catch { /* the tap-to-copy toast below covers it */ }
      try {
        // text, not url: apps that take files keep the text caption, and
        // Safari/Chrome both carry it — `url` is dropped by more targets.
        // Hashtags ride along for the apps that keep text (Messages,
        // WhatsApp, Facebook).
        await navigator.share({ files: payload, title: source.title, text: `${source.hashtags.join(' ')} ${url}` });
        if (captionCopied) {
          showToast('Caption with hashtags copied — paste it into your post', { type: 'success' });
        } else {
          // A fresh tap brings fresh clipboard permission
          showToast('Tap to copy the caption + hashtags for your post', {
            onClick: () => navigator.clipboard.writeText(sourceCaption(source, url)).catch(() => {}),
          });
        }
        return 'shared';
      } catch (err) {
        if (err?.name === 'AbortError') return 'dismissed';
        // e.g. NotAllowedError (activation expired) — try the URL rung
      }
    }
  }
  if (navigator.share) {
    try {
      await navigator.share({ title: source.title, url });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'dismissed';
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/* ---------- the yoink source + wrappers (the original card flow) ---------- */

/** Describe one yoink row as a card source. */
export function yoinkCardSource(yoink) {
  const who = yoink.public_profiles;
  const whoName = who?.full_name || who?.username || '';
  return {
    baseName: `yoink-${slugify(yoink.title || '') || 'adventure'}`,
    title: yoink.title || 'An adventure',
    metaLine: [yoink.locations?.display_name, stampDate(yoink.created_at)]
      .filter(Boolean).join('  ·  '),
    byline: whoName ? `yoinked by ${whoName}` : '',
    stampDate: yoink.created_at,
    category: yoink.categories || null,
    photos: photoPaths(yoink),
    hashtags: hashtags(yoink),
  };
}

/** The yoink's paste-ready caption (see sourceCaption). */
export function shareCaption(yoink, url = '') {
  return sourceCaption(yoinkCardSource(yoink), url);
}

/** Render the yoink's cover card alone. */
export async function buildShareCard(yoink) {
  try {
    const source = yoinkCardSource(yoink);
    await loadFonts();
    const photo = source.photos[0] ? await loadPhoto(source.photos[0]) : null;
    return await renderCard(source, photo, { cover: true }, `${source.baseName}.jpg`);
  } catch {
    return null;
  }
}

/** Render the yoink's full card set (see buildCardSet). */
export function buildShareCardSet(yoink) {
  return buildCardSet(yoinkCardSource(yoink));
}

/** Share one yoink as cards (see shareCardSet). */
export function shareYoinkCard(yoink, url, prebuilt = null) {
  return shareCardSet(yoinkCardSource(yoink), url, prebuilt);
}
